import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Alert,
  ButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  CreateNewFolder as CreateNewFolderIcon,
  CloudUpload as CloudUploadIcon,
  GetApp as GetAppIcon,
  DriveFolderUpload as DriveFolderUploadIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ClientNotes from './ClientNotes';

const Clients = () => {
  const { user: currentUser } = useAuth();
  const [clients, setClients] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    internal_code: '',
    hosting_type: '',
    vpn_type: '',
    db_host: '',
    db_port: '',
    db_user: '',
    db_password: '',
    db_support_user: '',
    db_support_password: '',
    db_name_prod: '',
    db_name_test: '',
    smtp_host: '',
    smtp_port: '',
    smtp_ssl: 0,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
  });
  
  // Estado para controlar si se han cargado las notas
  const [notesLoaded, setNotesLoaded] = useState(false);
  
  // Estados para la gestión de archivos
  const [currentPath, setCurrentPath] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [newFolderName, setNewFolderName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    fetchClients();
  }, []);
  
  // Efecto para cargar archivos o notas cuando se abre el diálogo y se selecciona la pestaña correspondiente
  useEffect(() => {
    if (openDialog && selectedClient) {
      if (tabValue === 3) {
        fetchClientFiles();
      } else if (tabValue === 4) {
        setNotesLoaded(true);
      }
    }
  }, [openDialog, selectedClient, tabValue, currentPath]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setSelectedClient(client);
      setFormData(client);
    } else {
      setSelectedClient(null);
      setFormData({
        internal_code: '',
        hosting_type: '',
        vpn_type: '',
        db_host: '',
        db_port: '',
        db_user: '',
        db_password: '',
        db_support_user: '',
        db_support_password: '',
        db_name_prod: '',
        db_name_test: '',
        smtp_host: '',
        smtp_port: '',
        smtp_ssl: 0,
        smtp_user: '',
        smtp_password: '',
        smtp_from_email: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedClient(null);
    setTabValue(0);
    setCurrentPath('');
    setFiles([]);
    setNotesLoaded(false);
    setFolders([]);
    setError(null);
    setNotesLoaded(false);
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Función para ordenar archivos y carpetas
  const sortItems = (items, type, sortField, order) => {
    return [...items].sort((a, b) => {
      let valueA, valueB;
      
      // Determinar los valores a comparar según el campo de ordenamiento
      if (sortField === 'name') {
        valueA = a.name.toLowerCase();
        valueB = b.name.toLowerCase();
      } else if (sortField === 'size') {
        valueA = a.size || 0;
        valueB = b.size || 0;
      } else if (sortField === 'modified') {
        valueA = new Date(a.modified || 0).getTime();
        valueB = new Date(b.modified || 0).getTime();
      }
      
      // Aplicar el orden (ascendente o descendente)
      if (order === 'asc') {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });
  };
  
  // Funciones para la gestión de archivos
  const fetchClientFiles = async (path = null) => {
    if (!selectedClient) return;
    
    // Usar el path proporcionado o el currentPath del estado
    const pathToUse = path !== null ? path : currentPath;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/clients/${selectedClient.id}/files`,
        { params: { path: pathToUse } }
      );
      
      // Ordenar carpetas por nombre (siempre van primero)
      const sortedFolders = sortItems(response.data.folders || [], 'folder', 'name', 'asc');
      
      // Ordenar archivos según criterios seleccionados
      const sortedFiles = sortItems(response.data.files || [], 'file', sortBy, sortOrder);
      
      setFolders(sortedFolders);
      setFiles(sortedFiles);
      setTotalSize(response.data.totalSize || 0);
    } catch (error) {
      console.error('Error al obtener archivos del cliente:', error);
      setError('Error al cargar los archivos. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };
  
  // Función para cambiar el criterio de ordenamiento
  const handleSortChange = (field) => {
    if (sortBy === field) {
      // Si ya estamos ordenando por este campo, cambiar el orden
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Si es un nuevo campo, establecerlo y usar orden ascendente por defecto
      setSortBy(field);
      setSortOrder('asc');
    }
    
    // Reordenar los archivos con los nuevos criterios
    const sortedFiles = sortItems(files, 'file', field, sortOrder === 'asc' ? 'desc' : 'asc');
    setFiles(sortedFiles);
  };
  
  // Función para descargar un archivo
  const handleDownloadFile = (fileName) => {
    const token = localStorage.getItem('token');
    const downloadUrl = `${process.env.REACT_APP_API_URL}/api/clients/${selectedClient.id}/files/download?path=${encodeURIComponent(currentPath)}&fileName=${encodeURIComponent(fileName)}`;
    
    // Crear un enlace temporal y simular un clic para iniciar la descarga
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', fileName);
    
    // Agregar el token como parámetro de consulta
    if (token) {
      link.href = `${link.href}&token=${token}`;
    }
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Función para descargar una carpeta como ZIP
  const handleDownloadFolder = (folderName = null) => {
    // Si folderName es null, descargar la carpeta actual
    const pathToDownload = folderName 
      ? (currentPath ? `${currentPath}/${folderName}` : folderName)
      : currentPath;
    
    const token = localStorage.getItem('token');
    const downloadUrl = `${process.env.REACT_APP_API_URL}/api/clients/${selectedClient.id}/folders/download?path=${encodeURIComponent(pathToDownload)}`;
    
    // Crear un enlace temporal y simular un clic para iniciar la descarga
    const link = document.createElement('a');
    link.href = downloadUrl;
    
    // Agregar el token como parámetro de consulta
    if (token) {
      link.href = `${link.href}&token=${token}`;
    }
    
    link.setAttribute('download', `(${folderName}||${selectedClient.internal_code})`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleNavigateFolder = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
    // Llamamos a fetchClientFiles con el nuevo path directamente
    fetchClientFiles(newPath);
  };
  
  const handleNavigateBack = () => {
    const pathParts = currentPath.split('/');
    pathParts.pop();
    const newPath = pathParts.join('/');
    setCurrentPath(newPath);
    // Llamamos a fetchClientFiles con el nuevo path directamente
    fetchClientFiles(newPath);
  };
  
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('El nombre de la carpeta no puede estar vacío');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/clients/${selectedClient.id}/folders`,
        { 
          path: currentPath,
          folderName: newFolderName.trim() 
        }
      );
      
      setNewFolderName('');
      setShowNewFolderInput(false);
      fetchClientFiles();
    } catch (error) {
      console.error('Error al crear carpeta:', error);
      setError('Error al crear la carpeta. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteFolder = async (folderName) => {
    if (!window.confirm(`¿Está seguro de eliminar la carpeta ${folderName}?`)) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/clients/${selectedClient.id}/folders`,
        { 
          data: { 
            path: currentPath,
            folderName: folderName 
          } 
        }
      );
      
      fetchClientFiles();
    } catch (error) {
      console.error('Error al eliminar carpeta:', error);
      setError('Error al eliminar la carpeta. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };
    
  const handleDeleteFile = async (fileName) => {
    if (!window.confirm(`¿Está seguro de eliminar el archivo ${fileName}?`)) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/clients/${selectedClient.id}/files`,
        { 
          data: { 
            path: currentPath,
            fileName: fileName 
          } 
        }
      );
      
      fetchClientFiles();
    } catch (error) {
      console.error('Error al eliminar archivo:', error);
      setError('Error al eliminar el archivo. Por favor, intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };
  
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileUpload = (e) => {    
      // Guardar el archivo directamente en una variable local
      const file = e.target.files[0];
      if (file) {
        // Actualizar el estado y luego subir el archivo usando la variable local
        setSelectedFile(file);
        // Usar setTimeout para asegurar que la subida ocurra después de la actualización del estado
        setTimeout(() => {
          const formData = new FormData();
          formData.append('file', file);
          
          setLoading(true);
          setError(null);
          
          axios.post(
            `${process.env.REACT_APP_API_URL}/api/clients/${selectedClient.id}/files?path=${encodeURIComponent(currentPath)}`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          )
          .then(() => {
            setSelectedFile(null);
            document.getElementById('file-upload').value = '';
            fetchClientFiles();
          })
          .catch((error) => {
            console.error('Error al subir archivo:', error);
            if (error.response && error.response.status === 409) {
              setError(error.response.data.message || 'Ya existe un archivo con el mismo nombre en esta ubicación');
            } else {
              setError('Error al subir el archivo. Por favor, intente nuevamente.');
            }
          })
          .finally(() => {
            setLoading(false);
          });
        }, 0);
      }    
  };

  const handleSubmit = async () => {
    try {
      if (selectedClient) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/clients/${selectedClient.id}`,
          formData
        );
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/clients`, formData);
      }
      fetchClients();
      handleCloseDialog();
    } catch (error) {
      console.error('Ha ocurrido un error:', error);
      alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
    }
  };

  const handleDelete = async (clientId) => {
    if (window.confirm('Seguro de borrar este cliente?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/clients/${clientId}`);
        fetchClients();
      } catch (error) {
        console.error('Ha ocurrido un error:', error);
        alert('Ha ocurrido un error, por favor intenta más tarde nuevamente.');
      }
    }
  };

  const canModifyClient = () => {
    return ['Administrador', 'Sistemas'].includes(currentUser.role);
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" gutterBottom>
          Clientes
        </Typography>
        {canModifyClient() && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Agregar cliente
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Hosting</TableCell>
              <TableCell>VPN </TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map((client) => (
              <TableRow key={client.id}>
                <TableCell>{client.internal_code}</TableCell>
                <TableCell>{client.hosting_type}</TableCell>
                <TableCell>{client.vpn_type}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {canModifyClient() && (
                    <>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDialog(client)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(client.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedClient ? 'Editar cliente' : 'Agregar cliente'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="client tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="General" />
              <Tab label="Base de datos" />
              <Tab label="SMTP" />
              {selectedClient && <Tab label="Archivos" />}
              {selectedClient && <Tab label="Notas" />}
            </Tabs>
          </Box>
          
          {/* Tab 1: General */}
          {tabValue === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ID"
                  value={formData.internal_code}
                  onChange={(e) =>
                    setFormData({ ...formData, internal_code: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Hosting</InputLabel>
                  <Select
                    value={formData.hosting_type}
                    onChange={(e) =>
                      setFormData({ ...formData, hosting_type: e.target.value })
                    }
                  >
                    <MenuItem value="A definir">A definir</MenuItem>
                    <MenuItem value="Compartido">Compartido</MenuItem>
                    <MenuItem value="Dedicado">Dedicado</MenuItem>                    
                    <MenuItem value="Propio">Propio</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>VPN</InputLabel>
                  <Select
                    value={formData.vpn_type}
                    onChange={(e) =>
                      setFormData({ ...formData, vpn_type: e.target.value })
                    }
                  >
                    <MenuItem value="Ninguna">Ninguna</MenuItem>
                    <MenuItem value="OpenVPN">OpenVPN</MenuItem>
                    <MenuItem value="PPTP">PPTP</MenuItem>
                    <MenuItem value="Física">Física</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
          
          {/* Tab 2: Base de datos */}
          {tabValue === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Host"
                  value={formData.db_host}
                  onChange={(e) =>
                    setFormData({ ...formData, db_host: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Puerto"
                  type="number"
                  value={formData.db_port}
                  onChange={(e) =>
                    setFormData({ ...formData, db_port: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Usuario"
                  value={formData.db_user}
                  onChange={(e) =>
                    setFormData({ ...formData, db_user: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contraseña"
                  type="text"
                  value={formData.db_password}
                  onChange={(e) =>
                    setFormData({ ...formData, db_password: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Usuario de soporte"
                  value={formData.db_support_user}
                  onChange={(e) =>
                    setFormData({ ...formData, db_support_user: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contraseña de soporte"
                  type="text"
                  value={formData.db_support_password}
                  onChange={(e) =>
                    setFormData({ ...formData, db_support_password: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre de la BD producción"
                  value={formData.db_name_prod}
                  onChange={(e) =>
                    setFormData({ ...formData, db_name_prod: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre de la BD Testing"
                  value={formData.db_name_test}
                  onChange={(e) =>
                    setFormData({ ...formData, db_name_test: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          )}
          
          {/* Tab 3: SMTP */}
          {tabValue === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Host"
                  value={formData.smtp_host}
                  onChange={(e) =>
                    setFormData({ ...formData, smtp_host: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Puerto"
                  type="number"
                  value={formData.smtp_port}
                  onChange={(e) =>
                    setFormData({ ...formData, smtp_port: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>SMTP SSL</InputLabel>
                  <Select
                    value={formData.smtp_ssl}
                    onChange={(e) =>
                      setFormData({ ...formData, smtp_ssl: e.target.value })
                    }
                  >
                    <MenuItem value={1}>Yes</MenuItem>
                    <MenuItem value={0}>No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Usuario"
                  value={formData.smtp_user}
                  onChange={(e) =>
                    setFormData({ ...formData, smtp_user: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contraseña"
                  type="text"
                  value={formData.smtp_password}
                  onChange={(e) =>
                    setFormData({ ...formData, smtp_password: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Remitente"
                  type="email"
                  value={formData.smtp_from_email}
                  onChange={(e) =>
                    setFormData({ ...formData, smtp_from_email: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          )}
          
          {/* Tab 4: Archivos */}
          {tabValue === 3 && selectedClient && (
            <Box>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              
              {/* Información de la carpeta actual */}
              <Box sx={{ mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {currentPath ? currentPath : 'Carpeta raíz'}
                </Typography>
                <Typography variant="body2">
                  Tamaño total: {formatFileSize(totalSize)}
                </Typography>
              </Box>
              
              {/* Barra de herramientas */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>              

                {/* Botón para volver */}
                <Button 
                  variant="outlined" 
                  onClick={handleNavigateBack} 
                  disabled={!currentPath}
                  size="small"
                  startIcon={<DriveFolderUploadIcon />}
                ></Button>

                
                {/* Botón para descargar carpeta */}
                <Button 
                  variant="outlined" 
                  color="secondary"
                  onClick={() => handleDownloadFolder(null)}
                  size="small"
                  startIcon={<GetAppIcon />}
                >
                  Descargar
                </Button>
                
                {/* Botón para nueva carpeta */}
                <Button
                  variant="outlined" 
                  size="small"                  
                  startIcon={<CreateNewFolderIcon />}
                  onClick={() => setShowNewFolderInput(!showNewFolderInput)}
                >
                  Crear
                </Button>
                
                {/* Botón para subir archivo */}
                <input
                  accept="*/*"
                  style={{ display: 'none' }}
                  id="file-upload"
                  type="file"
                  onChange={(e) => { handleFileUpload(e) }}
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    size="small"
                    startIcon={<CloudUploadIcon />}
                  >
                    Subir archivo
                  </Button>
                </label>
              </Box>
              
              {/* Input para nueva carpeta */}
              {showNewFolderInput && (
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Nombre de la carpeta"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleCreateFolder}
                    disabled={loading}
                  >
                    Crear
                  </Button>
                </Box>
              )}
              
              {/* Indicador de carga */}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              
              {/* Lista de archivos y carpetas */}
              {!loading && (
                <>
                  {folders.length === 0 && files.length === 0 ? (
                    <Typography variant="body1" sx={{ textAlign: 'center', p: 3 }}>
                      No hay archivos ni carpetas en esta ubicación
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: '50vh', overflow: 'auto' }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow sx={{ whiteSpace: 'nowrap' }}>
                            <TableCell 
                              onClick={() => handleSortChange('name')}
                              sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                            >
                              Nombre {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                            </TableCell>
                            <TableCell 
                              onClick={() => handleSortChange('size')}
                              sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                            >
                              Tamaño {sortBy === 'size' && (sortOrder === 'asc' ? '↑' : '↓')}
                            </TableCell>
                            <TableCell 
                              onClick={() => handleSortChange('modified')}
                              sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                            >
                              Fecha {sortBy === 'modified' && (sortOrder === 'asc' ? '↑' : '↓')}
                            </TableCell>
                            <TableCell align="center">Acciones</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {/* Carpetas */}
                          {folders.map((folder) => (
                            <TableRow key={folder.name} hover sx={{ position:'relative' }}>
                              <TableCell 
                                onClick={() => handleNavigateFolder(folder.name)}
                                sx={{ 
                                  cursor: 'pointer'
                                }}
                              >
                                <FolderIcon color="primary" sx={{ mr: 1, fontSize: 20, position: 'absolute' }} />
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 'medium',                                  
                                    ml: 3
                                  }}
                                >
                                  {folder.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{formatFileSize(folder.size)}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {folder.modified ? new Date(folder.modified).toLocaleString() : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton color="primary" size="small" onClick={() => handleDownloadFolder(folder.name)}>
                                  <GetAppIcon fontSize="small" />
                                </IconButton>
                                <IconButton color="error" size="small" onClick={() => handleDeleteFolder(folder.name)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                          
                          {/* Archivos */}
                          {files.map((file) => (
                            <TableRow key={file.name} hover sx={{ position:'relative', whiteSpace: 'nowrap' }}>
                              <TableCell sx={{ 
                              }}>
                                <FileIcon sx={{ mr: 1, fontSize: 20, position: 'absolute' }} />
                                <Typography 
                                  variant="body2" 
                                  sx={{                                     
                                    ml: 3
                                  }}
                                >
                                  {file.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{formatFileSize(file.size)}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {file.modified ? new Date(file.modified).toLocaleString() : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <IconButton color="primary" size="small" onClick={() => handleDownloadFile(file.name)}>
                                  <GetAppIcon fontSize="small" />
                                </IconButton>
                                <IconButton color="error" size="small" onClick={() => handleDeleteFile(file.name)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </Box>
          )}
          
          {/* Tab 5: Notas */}
          {tabValue === 4 && selectedClient && notesLoaded && (
            <Box sx={{ p: 1 }}>
              <ClientNotes clientId={selectedClient.id} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Clients;